import { useMutation, useQuery } from "@tanstack/react-query";
import axiosConfig from "../axiosInstance";
import store from "../../redux/store";
import { loginSuccess } from "../../redux/slices/authSlice";
import axios from "axios";
import { backendUrl } from "../../helpers/constants";

export const useRegisterUser = () =>
  useMutation({
    mutationKey: ["registerUser"],
    mutationFn: async (formData) =>
      await axiosConfig.post("/merchant/auth/register", formData),
  });

export const useLoginUser = () =>
  useMutation({
    mutationKey: ["loginUser"],
    mutationFn: async (formData) =>
      await axiosConfig.post("/merchant/auth/login", formData),
  });

export const useEmailVerification = () =>
  useMutation({
    mutationKey: ["emailVerification"],
    mutationFn: async ({ token }) =>
      await axiosConfig.post(`/merchant/verify-email?token=${token}`),
  });

export const useResendVerificationEmail = () =>
  useMutation({
    mutationKey: ["resendVerificationEmail"],
    mutationFn: async ({ email }) =>
      await axiosConfig.post(`/merchant/resend-verification-email`, { email }),
  });

export const useEditUserProfile = () =>
  useMutation({
    mutationKey: ["editUserProfile"],
    mutationFn: async (formData) =>
      await axiosConfig.put("/merchant/auth/update", formData),
  });

export const useChangePassword = () =>
  useMutation({
    mutationKey: ["changePassword"],
    mutationFn: async (formData) =>
      await axiosConfig.put("/merchant/auth/changePassword", formData),
  });

export const useVerifyUser = (path) =>
  useQuery({
    retry: 1,
    queryKey: ["verifyUser", { path }],
    queryFn: async () => {
      try {
        const { data } = await axiosConfig.get("/merchant/auth/getMe");
        const token = store.getState().auth.token;

        if (data) {
          store.dispatch(
            loginSuccess({
              data: data,
              token,
            })
          );
          return data;
        } else {
          return null;
        }
      } catch (err) {
        return null;
      }
    },
    refetchInterval: 60 * 1000,
  });

export const useForgotPassword = () =>
  useMutation({
    mutationKey: ["forgotPassword"],
    mutationFn: async (formData) =>
      await axios.post(
        `${backendUrl}/merchant/auth/forgettenPassword`,
        formData
      ),
  });

export const useVerifyResetOTP = () =>
  useMutation({
    mutationKey: ["verifyResetOTP"],
    mutationFn: async (token) =>
      await axiosConfig.get(`/merchant/auth/verifyToken/${token}`),
  });

export const useResetPassword = () =>
  useMutation({
    mutationKey: ["resetPassword"],
    mutationFn: async (formData) =>
      await axiosConfig.post(`/merchant/auth/resetPassword`, formData),
  });

export const useGetMerchants = (filters) =>
  useQuery({
    queryKey: ["getMerchants", { ...filters }],
    queryFn: async () =>
      await axiosConfig.get("/merchant/auth/merchant/users", {
        params: filters,
      }),
  });

export const useGetSingleMerchant = (id) =>
  useQuery({
    queryKey: ["getSingleMerchant", id],
    queryFn: async () =>
      await axiosConfig.get(`/merchant/auth/merchant/${id}`),
  });

export const useGetSingleSubMerchant = (id) => 
  useQuery({
    queryKey: ["getAMerchant", id],
    queryFn: async () => 
      await axiosConfig.get(`/merchant/auth/merchant/user/${id}`),
  })

export const useAddSubMerchant = () =>
  useMutation({
    mutationKey: ["addSubMerchant"],
    mutationFn: async (formData) =>
      await axiosConfig.post(`/merchant/subMerchant/register`, formData),
  });

export const useEditSubMerchant = (subMerchantId) =>
  useMutation({
    mutationKey: ["editSubMerchant", subMerchantId],
    mutationFn: async (formData) =>
      await axiosConfig.put(
        `/merchant/auth/subMerchant/${subMerchantId}`,
        formData
      ),
  });

export const useUpdateSubMerchantPassword = (subMerchantId) =>
  useMutation({
    mutationKey: ["updateSubMerchantPassword", subMerchantId],
    mutationFn: async (formData) =>
      await axiosConfig.put(
        `/merchant/auth/subMerchant/${subMerchantId}/password`,
        formData
      ),
  });

export const useDeleteSubMerchant = () =>
  useMutation({
    mutationKey: ["deleteSubMerchant"],
    mutationFn: async (id) =>
      await axiosConfig.delete(`/merchant/auth/merchant/user/${id}`),
  });

export const useGetAllRoles = () =>
  useQuery({
    queryKey: ["getAllROles"],
    queryFn: async () => await axiosConfig.get("/merchantRole/all"),
  });

export const useToggleSubMerchantMobileAccess = () =>
  useMutation({
    mutationKey: ["toggleSubMerchantMobileAccess"],
    mutationFn: async (subMerchantId) =>
      await axiosConfig.put(
        `/merchant/auth/mobile-login-for-user/${subMerchantId}`
      ),
  });
